import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  addNews,
  uploadNewsImage,
  clearImageUrl,
  resetNewsState,
} from "../../actions/NewsActions";
import RichTextEditor from "../../components/RichTextEditor";
import DOMPurify from "dompurify";
import "../../styles/NewsCreate.css";

function NewsCreate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, success, uploadedImageUrl } = useSelector(
    (state) => state.news,
  );

  const [form, setForm] = useState({
    slug: "",
    title: "",
    date: "",
    mainImage: "",
    paragraphs: "",
  });

  const [showPreview, setShowPreview] = useState(false);
  const isClearingRef = useRef(false);
  const hasClearedAfterSuccess = useRef(false);
  const editorRef = useRef();

  useEffect(() => {
    const savedData = localStorage.getItem("tempNewsData");
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setForm(parsed);
      } catch (e) {
        console.error("Failed to parse saved data", e);
      }
    }
  }, []);

  useEffect(() => {
    if (isClearingRef.current) return;
    const isEmpty = !form.slug && !form.title && !form.paragraphs;
    if (!isEmpty && !success) {
      localStorage.setItem("tempNewsData", JSON.stringify(form));
    }
  }, [form, success]);

  useEffect(() => {
    if (uploadedImageUrl) {
      setForm((prev) => ({ ...prev, mainImage: uploadedImageUrl }));
      dispatch(clearImageUrl());
    }
  }, [uploadedImageUrl, dispatch]);

  useEffect(() => {
    if (success && !hasClearedAfterSuccess.current) {
      hasClearedAfterSuccess.current = true;
      isClearingRef.current = true;
      localStorage.removeItem("tempNewsData");
      setForm({ slug: "", title: "", date: "", mainImage: "", paragraphs: "" });
      dispatch(clearImageUrl());
      dispatch(resetNewsState());
      navigate("/");
    }
  }, [success, dispatch, navigate]);

  const handleSideBySide = () => {
    const quill = editorRef.current?.getEditor();
    if (!quill) {
      alert("Editor not ready");
      return;
    }
    const range = quill.getSelection();
    if (!range || range.length === 0) {
      alert("Select two or more images (click and drag over them).");
      return;
    }
    const contents = quill.getContents(range.index, range.length);
    const images = [];
    contents.ops.forEach((op) => {
      if (op.insert && op.insert.image) images.push(op.insert.image);
    });
    if (images.length < 2) {
      alert(`Found only ${images.length} image(s). Select at least two.`);
      return;
    }
    quill.deleteText(range.index, range.length);
    let currentIndex = range.index;
    images.forEach((url) => {
      quill.insertEmbed(currentIndex, "image", url);
      currentIndex++;
    });
    quill.setSelection(currentIndex);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!form.slug) {
        alert("Please enter a slug before uploading an image.");
        return;
      }
      dispatch(uploadNewsImage(file, form.slug));
    }
  };

  const clearForm = () => {
    isClearingRef.current = true;
    setForm({ slug: "", title: "", date: "", mainImage: "", paragraphs: "" });
    localStorage.removeItem("tempNewsData");
    setTimeout(() => {
      isClearingRef.current = false;
    }, 100);
  };

  const formatDateForBackend = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allowedTags = [
      "p",
      "br",
      "strong",
      "em",
      "u",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "ul",
      "ol",
      "li",
      "a",
      "img",
      "div",
      "span",
    ];
    const allowedAttr = [
      "href",
      "src",
      "alt",
      "title",
      "width",
      "height",
      "class",
      "style",
    ];
    const cleanHtml = DOMPurify.sanitize(form.paragraphs, {
      ALLOWED_TAGS: allowedTags,
      ALLOWED_ATTR: allowedAttr,
    });
    const newsData = {
      slug: form.slug,
      title: form.title,
      date: formatDateForBackend(form.date),
      paragraphs: cleanHtml,
    };
    try {
      await dispatch(addNews(newsData));
    } catch (err) {
      console.error("Publish failed:", err);
    }
  };

  const allowedTags = [
    "p",
    "br",
    "strong",
    "em",
    "u",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    "a",
    "img",
    "div",
    "span",
  ];
  const allowedAttr = [
    "href",
    "src",
    "alt",
    "title",
    "width",
    "height",
    "class",
    "style",
  ];
  const previewContent = DOMPurify.sanitize(form.paragraphs, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: allowedAttr,
  });

  const months = [
    ["01", "January"],
    ["02", "February"],
    ["03", "March"],
    ["04", "April"],
    ["05", "May"],
    ["06", "June"],
    ["07", "July"],
    ["08", "August"],
    ["09", "September"],
    ["10", "October"],
    ["11", "November"],
    ["12", "December"],
  ];

  const updateDatePart = (partIndex, value) => {
    const parts = form.date ? form.date.split("-") : ["", "01", "01"];
    parts[partIndex] = value;
    setForm({ ...form, date: parts.join("-") });
  };

  const dateParts = form.date ? form.date.split("-") : ["", "", ""];

  return (
    <div className="news-create">
      <h1>Create News Article</h1>
      {error && <div className="error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Slug (unique, e.g., my-article)</label>
          <input
            type="text"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label>Date</label>
          <div className="date-selects">
            <select
              value={dateParts[0]}
              onChange={(e) => updateDatePart(0, e.target.value)}
              required
            >
              <option value="">Year</option>
              {Array.from(
                { length: 10 },
                (_, i) => new Date().getFullYear() - i,
              ).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <span className="date-separator">/</span>
            <select
              value={dateParts[1]}
              onChange={(e) => updateDatePart(1, e.target.value)}
              required
            >
              <option value="">Month</option>
              {months.map(([val, label]) => (
                <option key={val} value={val}>
                  {label}
                </option>
              ))}
            </select>
            <span className="date-separator">/</span>
            <select
              value={dateParts[2]}
              onChange={(e) => updateDatePart(2, e.target.value)}
              required
            >
              <option value="">Day</option>
              {Array.from({ length: 31 }, (_, i) =>
                String(i + 1).padStart(2, "0"),
              ).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Main Image</label>
          <input type="file" accept="image/*" onChange={handleImageUpload} />
          {isLoading && <div>Uploading...</div>}
          {form.mainImage && (
            <img
              src={`${form.mainImage}?t=${Date.now()}`}
              alt="preview"
              className="image-preview"
            />
          )}
        </div>

        <div className="form-group">
          <label>Article Content</label>
          <RichTextEditor
            ref={editorRef}
            value={form.paragraphs}
            onChange={(html) => setForm({ ...form, paragraphs: html })}
            slug={form.slug}
          />
        </div>

        <div className="button-group">
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className="preview-btn"
          >
            Preview
          </button>
          <button type="button" onClick={clearForm} className="clear-btn">
            Clear Form
          </button>
          <button type="submit" disabled={isLoading}>
            {isLoading ? "Publishing..." : "Publish News"}
          </button>
          <button
            type="button"
            onClick={handleSideBySide}
            className="side-by-side-btn"
          >
            🖼️ Side by Side
          </button>
        </div>
      </form>

      {showPreview && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Preview: {form.title || "Untitled"}</h2>
              <button
                className="close-btn"
                onClick={() => setShowPreview(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div
                className="preview-html"
                dangerouslySetInnerHTML={{ __html: previewContent }}
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowPreview(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NewsCreate;
